import React, { useCallback, useMemo, useRef, useState } from 'react';
import Map, { Layer, MapRef, NavigationControl, Source } from 'react-map-gl/maplibre';
import maplibregl from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';

import { API_BASE_URL } from '../../config/apiConfig';
import { SessionManager } from '../../redux/sessionManager';
import { useAppSelector, useAppDispatch } from '../../redux/hooks';
import { setSelectedComponents } from '../../redux/slices/uiSlice';
import { getLayerStyle, getSortedLayerTypes, SELECTION_COLOR } from './mapStyles';
import LayerControlPanel, { type LayerInfo } from './LayerControlPanel';

interface SiteMap3DProps {
  siteId: string;
  layers: LayerInfo[];
  bbox: [number, number, number, number]; // [minx, miny, maxx, maxy]
  center: [number, number]; // [lon, lat]
}

const TERRAIN_SOURCE = 'https://s3.amazonaws.com/elevation-tiles-prod/terrarium/{z}/{x}/{y}.png';

/**
 * Build a zoom-interpolated opacity expression for scale-based rendering.
 * Returns full opacity between minZoom and maxZoom, fading in/out at boundaries.
 */
function zoomOpacity(baseOpacity: number, minZoom: number, maxZoom: number): any {
  if (minZoom <= 0 && maxZoom >= 24) {
    return baseOpacity;
  }
  // step expression: 0 below minZoom, baseOpacity at minZoom, 0 at maxZoom
  const stops: any[] = ['step', ['zoom']];
  if (minZoom > 0) {
    stops.push(0, minZoom, baseOpacity);
  } else {
    stops.push(baseOpacity);
  }
  if (maxZoom < 24) {
    stops.push(maxZoom, 0);
  }
  return stops;
}

const SiteMap3D: React.FC<SiteMap3DProps> = ({ siteId, layers, bbox, center }) => {
  const mapRef = useRef<MapRef>(null);
  const dispatch = useAppDispatch();
  const selectedComponents = useAppSelector((state) => state.ui.selectedComponents);

  const [visibleLayers, setVisibleLayers] = useState<Set<string>>(
    () => new Set(layers.map((l) => l.type))
  );
  const [terrainEnabled, setTerrainEnabled] = useState(false);
  const [labelsEnabled, setLabelsEnabled] = useState(true);
  const [popupInfo, setPopupInfo] = useState<{
    lng: number;
    lat: number;
    properties: Record<string, any>;
  } | null>(null);

  // MVT tile URL with auth token
  const tileUrl = useMemo(() => {
    const authHeaders = SessionManager.getAuthHeaders();
    const token = authHeaders['Authorization']?.replace('Bearer ', '') || '';
    return `${API_BASE_URL}/api/v1/spatial/sites/${siteId}/tiles/{z}/{x}/{y}.mvt?token=${token}`;
  }, [siteId]);

  // Initial view from bbox
  const initialViewState = useMemo(() => ({
    longitude: center[0],
    latitude: center[1],
    zoom: 14,
    pitch: 45,
    bearing: 0,
  }), [center]);

  const handleToggleLayer = useCallback((layerType: string) => {
    setVisibleLayers((prev) => {
      const next = new Set(prev);
      if (next.has(layerType)) {
        next.delete(layerType);
      } else {
        next.add(layerType);
      }
      return next;
    });
  }, []);

  const handleToggleAll = useCallback(
    (visible: boolean) => {
      if (visible) {
        setVisibleLayers(new Set(layers.map((l) => l.type)));
      } else {
        setVisibleLayers(new Set());
      }
    },
    [layers]
  );

  const handleToggleTerrain = useCallback(() => {
    setTerrainEnabled((prev) => !prev);
  }, []);

  const handleToggleLabels = useCallback(() => {
    setLabelsEnabled((prev) => !prev);
  }, []);

  // Feature click handler — toggles selection in Redux for cross-tab sync
  const handleClick = useCallback(
    (e: maplibregl.MapMouseEvent) => {
      const map = mapRef.current?.getMap();
      if (!map) return;

      // Query selectable layers only
      const selectableLayerIds = layers
        .filter((l) => getLayerStyle(l.type).selectable)
        .flatMap((l) => [`spatial-fill-${l.type}`, `spatial-line-${l.type}`])
        .filter((id) => map.getLayer(id));

      // Also query non-selectable for popup
      const allLayerIds = layers
        .flatMap((l) => [`spatial-fill-${l.type}`, `spatial-line-${l.type}`])
        .filter((id) => map.getLayer(id));

      // Try selectable first
      const selectableFeatures = selectableLayerIds.length > 0
        ? map.queryRenderedFeatures(e.point, { layers: selectableLayerIds })
        : [];

      if (selectableFeatures.length > 0) {
        const feat = selectableFeatures[0];
        const entityId = feat.properties?.entity_id;

        if (entityId) {
          const current = selectedComponents;
          if (current.includes(entityId)) {
            dispatch(setSelectedComponents(current.filter((id) => id !== entityId)));
          } else {
            dispatch(setSelectedComponents([...current, entityId]));
          }
        }

        setPopupInfo({
          lng: e.lngLat.lng,
          lat: e.lngLat.lat,
          properties: feat.properties || {},
        });
        return;
      }

      // Fall through to non-selectable for popup only
      const allFeatures = allLayerIds.length > 0
        ? map.queryRenderedFeatures(e.point, { layers: allLayerIds })
        : [];

      if (allFeatures.length > 0) {
        setPopupInfo({
          lng: e.lngLat.lng,
          lat: e.lngLat.lat,
          properties: allFeatures[0].properties || {},
        });
      } else {
        setPopupInfo(null);
        if (selectedComponents.length > 0) {
          dispatch(setSelectedComponents([]));
        }
      }
    },
    [layers, selectedComponents, dispatch]
  );

  // Build selected entity_id set for filter expressions
  const selectedSet = useMemo(() => new Set(selectedComponents), [selectedComponents]);

  // Build map layers sorted by renderOrder (bottom to top) with scale-based visibility
  const mapLayers = useMemo(() => {
    const result: React.ReactNode[] = [];
    const sortedTypes = getSortedLayerTypes(layers.map((l) => l.type));

    for (const layerType of sortedTypes) {
      const style = getLayerStyle(layerType);
      const visible = visibleLayers.has(layerType);
      const baseFilter: any[] = ['==', ['get', 'layer_type'], layerType];

      // Compute opacity: 0 if hidden by toggle, zoom-interpolated if visible
      const fillOp = visible ? zoomOpacity(style.opacity, style.minZoom, style.maxZoom) : 0;
      const outlineOp = visible ? zoomOpacity(Math.min(style.opacity + 0.2, 1), style.minZoom, style.maxZoom) : 0;
      const lineOp = visible ? zoomOpacity(style.opacity, style.minZoom, style.maxZoom) : 0;

      if (style.type === 'fill') {
        // --- Fill layer ---
        result.push(
          <Layer
            key={`fill-${layerType}`}
            id={`spatial-fill-${layerType}`}
            type="fill"
            source="spatial-tiles"
            source-layer="spatial"
            filter={baseFilter}
            paint={{
              'fill-color': style.color,
              'fill-opacity': fillOp,
            }}
          />
        );

        // --- Outline ---
        result.push(
          <Layer
            key={`outline-${layerType}`}
            id={`spatial-outline-${layerType}`}
            type="line"
            source="spatial-tiles"
            source-layer="spatial"
            filter={baseFilter}
            paint={{
              'line-color': style.outlineColor || style.color,
              'line-opacity': outlineOp,
              'line-width': 1,
            }}
          />
        );

        // --- Selection highlight (on top) ---
        if (style.selectable && selectedSet.size > 0) {
          const selectedFilter: any[] = [
            'all',
            ['==', ['get', 'layer_type'], layerType],
            ['in', ['get', 'entity_id'], ['literal', [...selectedSet]]],
          ];

          const selFillOp = visible ? zoomOpacity(0.7, style.minZoom, style.maxZoom) : 0;
          const selLineOp = visible ? zoomOpacity(1, style.minZoom, style.maxZoom) : 0;

          result.push(
            <Layer
              key={`fill-selected-${layerType}`}
              id={`spatial-fill-selected-${layerType}`}
              type="fill"
              source="spatial-tiles"
              source-layer="spatial"
              filter={selectedFilter}
              paint={{
                'fill-color': SELECTION_COLOR,
                'fill-opacity': selFillOp,
              }}
            />
          );

          result.push(
            <Layer
              key={`outline-selected-${layerType}`}
              id={`spatial-outline-selected-${layerType}`}
              type="line"
              source="spatial-tiles"
              source-layer="spatial"
              filter={selectedFilter}
              paint={{
                'line-color': SELECTION_COLOR,
                'line-opacity': selLineOp,
                'line-width': 4,
              }}
            />
          );
        }
      } else {
        // --- Line layer ---
        result.push(
          <Layer
            key={`line-${layerType}`}
            id={`spatial-line-${layerType}`}
            type="line"
            source="spatial-tiles"
            source-layer="spatial"
            filter={baseFilter}
            paint={{
              'line-color': style.color,
              'line-opacity': lineOp,
              'line-width': style.width || 2,
            }}
          />
        );
      }
    }

    // --- Label layers (on top of everything) ---
    // Symbol layers on polygon features auto-place at centroid.
    // symbol-sort-key by id ensures deterministic dedup (one label per region).
    if (labelsEnabled) {
      for (const layerType of sortedTypes) {
        const style = getLayerStyle(layerType);
        if (style.type !== 'fill') continue;
        const visible = visibleLayers.has(layerType);

        const labelFilter: any[] = [
          'all',
          ['==', ['get', 'layer_type'], layerType],
          ['has', 'label'],
          ['!=', ['get', 'label'], ''],
        ];

        // Show labels 2 zoom levels after the fill appears
        const labelMinZoom = style.minZoom + 2;
        const labelOp = visible ? zoomOpacity(1, labelMinZoom, style.maxZoom) : 0;

        result.push(
          <Layer
            key={`label-${layerType}`}
            id={`spatial-label-${layerType}`}
            type="symbol"
            source="spatial-tiles"
            source-layer="spatial"
            filter={labelFilter}
            layout={{
              'text-field': ['get', 'label'],
              'text-size': 11,
              'text-font': ['Open Sans Regular', 'Arial Unicode MS Regular'],
              'text-anchor': 'center',
              'text-allow-overlap': false,
              'text-ignore-placement': false,
              'text-padding': 4,
              'symbol-placement': 'point',
              'symbol-sort-key': ['get', 'id'],
            }}
            paint={{
              'text-color': '#ffffff',
              'text-halo-color': '#000000',
              'text-halo-width': 1.5,
              'text-opacity': labelOp,
            }}
          />
        );
      }
    }

    return result;
  }, [layers, visibleLayers, selectedSet, labelsEnabled]);

  return (
    <div className="relative w-full h-full">
      <Map
        ref={mapRef}
        mapLib={maplibregl as any}
        initialViewState={initialViewState}
        style={{ width: '100%', height: '100%' }}
        mapStyle={{
          version: 8,
          sources: {
            'esri-satellite': {
              type: 'raster',
              tiles: [
                'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
              ],
              tileSize: 256,
              attribution: '&copy; Esri',
              maxzoom: 19,
            },
            ...(terrainEnabled
              ? {
                  'terrain-source': {
                    type: 'raster-dem',
                    tiles: [TERRAIN_SOURCE],
                    tileSize: 256,
                    encoding: 'terrarium',
                    maxzoom: 15,
                  },
                }
              : {}),
          },
          layers: [
            {
              id: 'satellite',
              type: 'raster',
              source: 'esri-satellite',
              minzoom: 0,
              maxzoom: 22,
            },
          ],
          ...(terrainEnabled
            ? {
                terrain: {
                  source: 'terrain-source',
                  exaggeration: 1.5,
                },
              }
            : {}),
        } as any}
        onClick={handleClick}
        maxZoom={22}
        minZoom={4}
        interactiveLayerIds={layers
          .filter((l) => getLayerStyle(l.type).selectable)
          .map((l) => `spatial-fill-${l.type}`)}
      >
        <NavigationControl position="top-left" visualizePitch />

        <Source
          id="spatial-tiles"
          type="vector"
          tiles={[tileUrl]}
          minzoom={4}
          maxzoom={22}
        >
          {mapLayers}
        </Source>
      </Map>

      {/* Popup */}
      {popupInfo && (
        <div
          className="absolute z-20 bg-[var(--color-surface)] border border-[var(--color-border)] rounded-lg shadow-xl p-3 max-w-xs"
          style={{
            left: '50%',
            bottom: 16,
            transform: 'translateX(-50%)',
          }}
        >
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-medium text-[var(--color-text-primary)]">
              {popupInfo.properties.layer_type || 'Feature'}
            </span>
            <button
              onClick={() => setPopupInfo(null)}
              className="text-[var(--color-text-tertiary)] hover:text-[var(--color-text-primary)] text-xs ml-4"
            >
              ✕
            </button>
          </div>
          <div className="space-y-1 text-xs text-[var(--color-text-secondary)]">
            {popupInfo.properties.label && (
              <div><span className="font-medium">Label:</span> {popupInfo.properties.label}</div>
            )}
            {popupInfo.properties.entity_id && (
              <div>
                <span className="font-medium">Entity:</span>{' '}
                <span className="text-blue-500">{popupInfo.properties.entity_id}</span>
              </div>
            )}
            <div className="text-[10px] text-[var(--color-text-tertiary)]">
              {popupInfo.lng.toFixed(6)}, {popupInfo.lat.toFixed(6)}
            </div>
          </div>
        </div>
      )}

      {/* Layer Controls */}
      <LayerControlPanel
        layers={layers}
        visibleLayers={visibleLayers}
        onToggleLayer={handleToggleLayer}
        onToggleAll={handleToggleAll}
        terrainEnabled={terrainEnabled}
        onToggleTerrain={handleToggleTerrain}
        labelsEnabled={labelsEnabled}
        onToggleLabels={handleToggleLabels}
      />
    </div>
  );
};

export default SiteMap3D;
